import { Component } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { OverlayEventDetail } from '@ionic/core';
import { ConnectionService } from 'src/app/core/connection/connection.service';
import { Appointment } from 'src/app/core/models/appointment.model';
import { Attachment, AttachmentFile, AttachmentUploadResponse } from 'src/app/core/models/attachment.model';
import { AppointmentService } from 'src/app/core/services/appointment/appointment.service';
import { LocalStorageService } from 'src/app/core/storage/local-storage/local-storage.service';
import { AppointmentAttachmentAddComponent } from '../appointment-attachment-add/appointment-attachment-add.component';

type AddAttachmentModalResponse = OverlayEventDetail<{
	attachmentUpload?: AttachmentUploadResponse;
	dismissed?: boolean;
}>;

@Component({
	selector: 'app-appointment-attachment-list',
	templateUrl: './appointment-attachment-list.component.html',
	styleUrls: ['./appointment-attachment-list.component.scss'],
})
export class AppointmentAttachmentListComponent {

	pageTitle = 'Attachments';

	attachments: Attachment[];
	appointment: Appointment;

	constructor(
		private storage: LocalStorageService,
		private modalController: ModalController,
		private appointmentService: AppointmentService,
		private connectionService: ConnectionService,
		private alertController: AlertController,
	) { }

	// Called by parent layout
	setAppointment(appointment: Appointment) {
		this.appointment = appointment;
		if (!appointment) {
			return;
		}

		this.getAttachments();
	}

	async getAttachments(): Promise<void> {
		const appointment = await this.storage.appointments.getItem<Appointment>(this.appointment.id);
		if (!appointment) {
			this.attachments = [];
			return;
		}

		this.attachments = this.sortAttachments(appointment.attachments);
	}

	async downloadFile(attachment: AttachmentFile) {
		if (!this.connectionService.isOnline$.value) {
			await this.alertOffline();
			return;
		}

		await this.appointmentService.downloadAttachment(this.appointment.id, attachment);
	}

	sortAttachments(attachments: Attachment[]) {
		// Sort files in groups
		attachments.map(attachment => attachment.files.sort((a, b) => a.filename > b.filename ? 1 : -1));
		// Sort groups
		attachments.sort((a, b) => a.group > b.group ? 1 : -1);

		return attachments;
	}

	async onClickGoToAddAttachment(): Promise<void> {
		const modal = await this.modalController.create({
			component: AppointmentAttachmentAddComponent,
			componentProps: {
				appointmentId: this.appointment.id,
			},
			cssClass: 'full-screen-modal',
		});

		await modal.present();
		const response = await modal.onDidDismiss() as AddAttachmentModalResponse;
		if (response?.data?.attachmentUpload === undefined) {
			return;
		}

		await this.addAttachmentFromUpload(response.data.attachmentUpload);
	}

	async addAttachmentFromUpload(attachmentUpload: AttachmentUploadResponse) {
		const appointment = await this.storage.appointments.getItem<Appointment>(this.appointment.id);

		let attachment: Attachment;
		const groupIndex = appointment.attachments.findIndex(attach => attach.group === attachmentUpload.group);
		if (groupIndex === -1) { // Not found
			attachment = {
				group: attachmentUpload.group,
				files: [
					{
						xrefId: attachmentUpload.xrefId,
						filename: attachmentUpload.filename,
					},
				],
			};
			appointment.attachments.push(attachment);
			await this.storage.appointments.setItem(`${appointment.id}`, appointment);
		} else {
			appointment.attachments[groupIndex].files.push({
				xrefId: attachmentUpload.xrefId,
				filename: attachmentUpload.filename,
			} as AttachmentFile);
			await this.storage.appointments.setItem(`${appointment.id}`, appointment);
		}

		await this.getAttachments();
	}

	private async alertOffline(): Promise<void> {
		const offlineAlert = await this.alertController.create({
			header: 'Device Offline',
			message: 'Download is not available while device is offline.',
			buttons: ['OK'],
		});

		await offlineAlert.present();
	}

}
